import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { GroupMenuDto, GroupMenuPayload } from '../../../../core/models/system-settings/group-menus.model';
import { GroupSubMenuDto, GroupSubMenuPayload } from '../../../../core/models/system-settings/group-sub-menus.model';
import { MenuListDto, MenuListPayload } from '../../../../core/models/system-settings/menu-lists.model';
import { AccessRole, AccessRoleDto } from '../../../../core/models/system-settings/manage-roles.model';
import {
  AccessMenuRole,
  AccessMenuRoleDto,
  RoleWithMenuCount,
  GroupSubWithMenuCount,
} from '../../../../core/models/system-settings/manage-roles-menu.model';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperManageRolesService } from '../../../../core/mappers/system-settings/mapper-manage-roles.service';
import { MapperManageRolesMenuService } from '../../../../core/mappers/system-settings/mapper-manage-roles-menu.service';
import { MapperGroupSubMenusService } from '../../../../core/mappers/system-settings/mapper-group-sub-menus.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ModalManageRoleMenuComponent } from '../../modals/system-settings/modal-manage-role-menu/modal-manage-role-menu.component';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-manage-roles-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalManageRoleMenuComponent],
  templateUrl: './manage-roles-menu.component.html',
  styleUrl: './manage-roles-menu.component.css'
})
export class ManageRolesMenuComponent implements OnInit {
  private apiService = inject(ApiSystemSettingsService);
  private roleMapper = inject(MapperManageRolesService);
  private menuRoleMapper = inject(MapperManageRolesMenuService);
  private groupSubMapper = inject(MapperGroupSubMenusService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private permissionService = inject(MenuPermissionService);

  rolesWithCount = signal<RoleWithMenuCount[]>([]);
  groupSubsWithCount = signal<GroupSubWithMenuCount[]>([]);
  menuRoles = signal<AccessMenuRole[]>([]);
  selectedRole = signal<RoleWithMenuCount | null>(null);
  selectedGroupSub = signal<GroupSubWithMenuCount | null>(null);

  isLoading = signal<boolean>(false);
  isDashboardOpen = signal<boolean>(true);
  searchText = signal<string>('');
  subSearchText = signal<string>('');
  menuSearchText = signal<string>('');

  isModalOpen = signal<boolean>(false);
  selectedMenuRole = signal<AccessMenuRole | null>(null);
  isDeleteModalOpen = signal<boolean>(false);
  itemToDelete = signal<AccessMenuRole | null>(null);

  // Import / Export State
  isExporting = signal<boolean>(false);
  isImportModalOpen = signal<boolean>(false);
  importMode = signal<'skip' | 'update' | 'force'>('skip');
  pendingImportData = signal<any>(null);
  selectedFileName = signal<string>('');
  importSummary = signal<{ groupMenusCount: number; groupSubMenusCount: number; menuListsCount: number }>({
    groupMenusCount: 0,
    groupSubMenusCount: 0,
    menuListsCount: 0
  });
  importProgress = signal<{ current: number; total: number; text: string }>({
    current: 0,
    total: 0,
    text: ''
  });

  dashboardStats = signal<any[]>([]);

  currentPage = signal<number>(1);
  pageSize = signal<number>(20);
  pageSizeOptions = [20, 50, 100];
  sortColumn = signal<string>('menuListName');
  sortDirection = signal<'asc' | 'desc'>('asc');

  Math = Math;

  activeRoles = computed(() => this.filteredRoles().filter(r => r.status));
  inactiveRoles = computed(() => this.filteredRoles().filter(r => !r.status));

  filteredRoles = computed(() => {
    const search = this.searchText().trim().toLowerCase();
    let list = this.rolesWithCount();
    if (search) {
      list = list.filter(r =>
        r.name.toLowerCase().includes(search) ||
        r.id.toString() === search
      );
    }
    return list;
  });

  filteredGroupSubs = computed(() => {
    const search = this.subSearchText().trim().toLowerCase();
    let list = this.groupSubsWithCount();
    if (search) {
      list = list.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.groupMenuName.toLowerCase().includes(search) ||
        s.id.toString() === search
      );
    }
    return [...list].sort((a, b) => {
      const menuCmp = a.groupMenuName.localeCompare(b.groupMenuName, 'th');
      if (menuCmp !== 0) return menuCmp;
      return a.number - b.number;
    });
  });

  groupedSubs = computed(() => {
    const map = new Map<string, GroupSubWithMenuCount[]>();
    for (const sub of this.filteredGroupSubs()) {
      const key = sub.groupMenuName || 'อื่นๆ';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(sub);
    }
    return Array.from(map.entries()).map(([groupMenuName, items]) => ({ groupMenuName, items }));
  });

  filteredMenuData = computed(() => {
    let filtered = this.menuRoles();
    const sub = this.selectedGroupSub();

    if (sub) {
      if (sub.id === 0) {
        filtered = filtered.filter(m => !m.groupSubId);
      } else {
        filtered = filtered.filter(m => m.groupSubId === sub.id);
      }
    }

    const search = this.menuSearchText().trim().toLowerCase();
    if (search) {
      filtered = filtered.filter(m =>
        m.menuListName.toLowerCase().includes(search) ||
        m.name.toLowerCase().includes(search) ||
        m.id.toString() === search
      );
    }

    const col = this.sortColumn();
    const dir = this.sortDirection();
    if (col) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let valA = a[col];
        let valB = b[col];
        if (typeof valA === 'string' && typeof valB === 'string') {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }
        if (valA < valB) return dir === 'asc' ? -1 : 1;
        if (valA > valB) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  });

  totalItems = computed(() => this.filteredMenuData().length);

  filteredAndPaginatedMenuData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredMenuData().slice(start, start + this.pageSize());
  });

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get isRolePickerView(): boolean {
    return this.selectedRole() === null;
  }

  get isGroupSubPickerView(): boolean {
    return this.selectedRole() !== null && this.selectedGroupSub() === null;
  }

  get isMenuTableView(): boolean {
    return this.selectedRole() !== null && this.selectedGroupSub() !== null;
  }

  ngOnInit() {
    this.loadRolesStep();
  }

  toggleDashboard(): void {
    this.isDashboardOpen.set(!this.isDashboardOpen());
  }

  onRefresh(): void {
    if (this.isRolePickerView) {
      this.searchText.set('');
      this.loadRolesStep();
    } else if (this.isGroupSubPickerView) {
      this.subSearchText.set('');
      this.loadGroupSubStep();
    } else {
      this.menuSearchText.set('');
      this.currentPage.set(1);
      this.reloadMenuTable();
    }
  }

  selectRole(role: RoleWithMenuCount): void {
    this.selectedRole.set(role);
    this.selectedGroupSub.set(null);
    this.subSearchText.set('');
    this.loadGroupSubStep();
  }

  selectGroupSub(sub: GroupSubWithMenuCount): void {
    this.selectedGroupSub.set(sub);
    this.menuSearchText.set('');
    this.currentPage.set(1);
    this.reloadMenuTable();
  }

  backToRolePicker(): void {
    this.selectedRole.set(null);
    this.selectedGroupSub.set(null);
    this.groupSubsWithCount.set([]);
    this.menuRoles.set([]);
    this.loadRolesStep();
  }

  backToGroupSubPicker(): void {
    this.selectedGroupSub.set(null);
    this.menuSearchText.set('');
    this.loadGroupSubStep();
  }

  navigateToCreateMenu(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedMenuRole.set(null);
    this.isModalOpen.set(true);
  }

  navigateToEditMenu(item: AccessMenuRole): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedMenuRole.set(item);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedMenuRole.set(null);
  }

  onDeleteMenu(item: AccessMenuRole): void {
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ');
      return;
    }

    this.itemToDelete.set(item);
    this.isDeleteModalOpen.set(true);
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen.set(false);
    this.itemToDelete.set(null);
  }

  confirmDeleteMenu(): void {
    const item = this.itemToDelete();
    if (!item) return;

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;

    this.isLoading.set(true);
    this.closeDeleteModal();

    this.apiService.deleteMenuRole(item.id, userId, menuId).subscribe({
      next: () => {
        this.toast.success('ลบข้อมูลสำเร็จ');
        this.onRefresh();
        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ลบข้อมูลไม่สำเร็จ');
        this.isLoading.set(false);
      }
    });
  }

  onSearchMenus(): void {
    this.currentPage.set(1);
  }

  sortData(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / (this.pageSize() || 1));
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage() - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
  }

  trackById(index: number, item: { id: number }): number {
    return item.id;
  }

  private calculateDashboardStats(roles: RoleWithMenuCount[]): void {
    const total = roles.length;
    const activeTotal = roles.filter(r => r.status).length;
    const totalMenus = roles.reduce((sum, r) => sum + r.menuCount, 0);

    this.dashboardStats.set([
      { title: 'Role ทั้งหมด', value: total.toLocaleString(), icon: 'badge', color: 'text-amber-500' },
      { title: 'เปิดใช้งาน', value: activeTotal.toLocaleString(), icon: 'check_circle', color: 'text-emerald-500' },
      { title: 'เมนูที่ผูกทั้งหมด', value: totalMenus.toLocaleString(), icon: 'menu_book', color: 'text-sky-500' },
    ]);
  }

  private loadRolesStep(): void {
    this.isLoading.set(true);

    this.apiService.getAccessRolesForTable().subscribe({
      next: (roleDtos: AccessRoleDto[]) => {
        const roles: AccessRole[] = (roleDtos || []).map(d => this.roleMapper.mapAccessRoleFromDto(d));

        this.apiService.getAccessMenuRolesForTable().subscribe({
          next: (menuRoleDtos: AccessMenuRoleDto[]) => {
            const counts: Record<number, number> = {};
            (menuRoleDtos || []).forEach(mr => {
              if (mr.roleId) {
                counts[mr.roleId] = (counts[mr.roleId] || 0) + 1;
              }
            });

            const withCount: RoleWithMenuCount[] = roles.map(r => ({
              id: r.id,
              name: r.name,
              status: r.status,
              menuCount: counts[r.id] || 0,
            }));

            this.rolesWithCount.set(withCount);
            this.calculateDashboardStats(withCount);
            this.isLoading.set(false);
          },
          error: () => {
            const withCount: RoleWithMenuCount[] = roles.map(r => ({
              id: r.id,
              name: r.name,
              status: r.status,
              menuCount: 0,
            }));
            this.rolesWithCount.set(withCount);
            this.calculateDashboardStats(withCount);
            this.isLoading.set(false);
          }
        });
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดข้อมูล Role ได้');
        this.isLoading.set(false);
      }
    });
  }

  private loadGroupSubStep(): void {
    const role = this.selectedRole();
    if (!role) return;

    this.isLoading.set(true);

    this.apiService.getGroupSubMenusForTable().subscribe({
      next: (subDtos: GroupSubMenuDto[]) => {
        const subs = (subDtos || []).map(d => this.groupSubMapper.mapGroupSubMenuFromDto(d));

        this.apiService.getAccessMenuRolesForTable(role.id).subscribe({
          next: (menuRoleDtos: AccessMenuRoleDto[]) => {
            const menuRoles = (menuRoleDtos || []).map(d => this.menuRoleMapper.mapAccessMenuRoleFromDto(d));
            this.menuRoles.set(menuRoles);

            const counts: Record<number, number> = {};
            let ungroupedCount = 0;
            menuRoles.forEach(mr => {
              if (mr.groupSubId) {
                counts[mr.groupSubId] = (counts[mr.groupSubId] || 0) + 1;
              } else {
                ungroupedCount++;
              }
            });

            const withCount: GroupSubWithMenuCount[] = subs.map(s => ({
              id: s.id,
              name: s.name,
              enable: s.enable,
              groupMenuId: s.groupMenuId,
              groupMenuName: s.groupMenuName,
              number: s.number,
              icon: s.icon,
              menuCount: counts[s.id] || 0,
            }));

            if (ungroupedCount > 0) {
              withCount.push({
                id: 0,
                name: 'ไม่ระบุกลุ่มย่อย',
                enable: true,
                groupMenuId: 0,
                groupMenuName: 'อื่นๆ',
                number: 9999,
                icon: 'help_outline',
                menuCount: ungroupedCount,
              });
            }

            this.groupSubsWithCount.set(withCount);
            this.isLoading.set(false);
          },
          error: () => {
            const withCount: GroupSubWithMenuCount[] = subs.map(s => ({
              id: s.id,
              name: s.name,
              enable: s.enable,
              groupMenuId: s.groupMenuId,
              groupMenuName: s.groupMenuName,
              number: s.number,
              icon: s.icon,
              menuCount: 0,
            }));
            this.groupSubsWithCount.set(withCount);
            this.menuRoles.set([]);
            this.isLoading.set(false);
          }
        });
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดกลุ่มเมนูย่อยได้');
        this.isLoading.set(false);
      }
    });
  }

  private reloadMenuTable(): void {
    const role = this.selectedRole();
    if (!role) return;

    this.isLoading.set(true);
    this.apiService.getAccessMenuRolesForTable(role.id).subscribe({
      next: (dtos: AccessMenuRoleDto[]) => {
        const list = (dtos || []).map(d => this.menuRoleMapper.mapAccessMenuRoleFromDto(d));
        this.menuRoles.set(list);

        const sub = this.selectedGroupSub();
        if (sub && sub.id !== 0) {
          const count = list.filter(m => m.groupSubId === sub.id).length;
          this.groupSubsWithCount.update(items =>
            items.map(item => item.id === sub.id ? { ...item, menuCount: count } : item)
          );
        }

        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error(err);
        this.toast.error('ไม่สามารถโหลดเมนูของ Role ได้');
        this.isLoading.set(false);
      }
    });
  }

  // ================= Import / Export Functions =================

  /**
   * Reads and parses uploaded JSON file for menu structure import.
   * @param event HTML File Input event
   */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.selectedFileName.set(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const parsed = JSON.parse(text);

        let hierarchy: any[] = [];
        if (parsed.hierarchy && Array.isArray(parsed.hierarchy)) {
          hierarchy = parsed.hierarchy;
        } else if (parsed.groupMenus && Array.isArray(parsed.groupMenus)) {
          hierarchy = parsed.groupMenus;
        } else if (Array.isArray(parsed)) {
          hierarchy = parsed;
        }

        if (hierarchy.length === 0) {
          this.toast.error('ไม่พบโครงสร้างข้อมูลเมนูในไฟล์ JSON');
          input.value = '';
          return;
        }

        let gCount = hierarchy.length;
        let subCount = 0;
        let menuCount = 0;

        hierarchy.forEach((g: any) => {
          if (g.subMenus && Array.isArray(g.subMenus)) {
            subCount += g.subMenus.length;
            g.subMenus.forEach((s: any) => {
              if (s.menuLists && Array.isArray(s.menuLists)) {
                menuCount += s.menuLists.length;
              }
            });
          }
          if (g.menuLists && Array.isArray(g.menuLists)) {
            menuCount += g.menuLists.length;
          }
        });

        this.pendingImportData.set({ hierarchy, original: parsed });
        this.importSummary.set({
          groupMenusCount: gCount,
          groupSubMenusCount: subCount,
          menuListsCount: menuCount
        });
        this.isImportModalOpen.set(true);
      } catch (err) {
        console.error(err);
        this.toast.error('ไฟล์ JSON ไม่ถูกต้อง กรุณาตรวจสอบรูปแบบไฟล์');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  /**
   * Closes the menu import modal.
   */
  public closeImportModal(): void {
    this.isImportModalOpen.set(false);
    this.pendingImportData.set(null);
    this.selectedFileName.set('');
    this.importProgress.set({ current: 0, total: 0, text: '' });
  }

  /**
   * Exports full menu structure (Group Menus, Group Sub Menus, Menu Lists) as a JSON file.
   */
  public exportMenuData(): void {
    this.isExporting.set(true);
    this.isLoading.set(true);

    forkJoin([
      this.apiService.getGroupMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getGroupSubMenusForTable().pipe(catchError(() => of([]))),
      this.apiService.getMenuListsForTable().pipe(catchError(() => of([])))
    ]).subscribe({
      next: ([groupDtos, subDtos, menuDtos]) => {
        const groupMap = new Map<number, any>();
        (groupDtos || []).forEach(g => {
          groupMap.set(g.id, {
            name: g.name,
            status: g.status ?? true,
            number: g.number || 1,
            subMenus: [],
            menuLists: []
          });
        });

        const subMap = new Map<number, any>();
        (subDtos || []).forEach(s => {
          const subObj = {
            name: s.name,
            enable: s.enable ?? true,
            number: s.number || 1,
            icon: s.icon || 'folder',
            menuLists: []
          };
          subMap.set(s.id, subObj);

          if (s.groupMenuId && groupMap.has(s.groupMenuId)) {
            groupMap.get(s.groupMenuId).subMenus.push(subObj);
          }
        });

        (menuDtos || []).forEach(m => {
          const menuObj = {
            name: m.name,
            status: m.status ?? true,
            path: m.path || '',
            pathId: m.pathId ?? null,
            number: m.number || 1
          };

          if (m.groupSubId && subMap.has(m.groupSubId)) {
            subMap.get(m.groupSubId).menuLists.push(menuObj);
          } else if (m.groupMenuId && groupMap.has(m.groupMenuId)) {
            groupMap.get(m.groupMenuId).menuLists.push(menuObj);
          }
        });

        const hierarchy = Array.from(groupMap.values());

        const exportData = {
          exportVersion: '1.0',
          exportedAt: new Date().toISOString(),
          summary: {
            groupMenusCount: groupDtos.length,
            groupSubMenusCount: subDtos.length,
            menuListsCount: menuDtos.length
          },
          hierarchy: hierarchy,
          raw: {
            groupMenus: groupDtos,
            groupSubMenus: subDtos,
            menuLists: menuDtos
          }
        };

        const jsonStr = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const now = new Date();
        const dateStr = now.toISOString().replace(/[-:T.]/g, '').slice(0, 14);

        a.href = url;
        a.download = `city_parking_menus_export_${dateStr}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.toast.success('ส่งออกข้อมูลเมนูสำเร็จแล้ว');
        this.isLoading.set(false);
        this.isExporting.set(false);
      },
      error: (err) => {
        console.error(err);
        this.toast.error('เกิดข้อผิดพลาดในการดึงข้อมูลเมนูเพื่อส่งออก');
        this.isLoading.set(false);
        this.isExporting.set(false);
      }
    });
  }

  /**
   * Executes step-by-step API calls to import hierarchical menu structure into database.
   */
  public async importMenuData(): Promise<void> {
    const importPayload = this.pendingImportData();
    if (!importPayload || !importPayload.hierarchy) return;

    this.isLoading.set(true);

    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
    const currentPath = this.router.url;
    const menuId = this.permissionService.getMenuId(currentPath) || 15;

    const hierarchy: any[] = importPayload.hierarchy;

    let totalTasks = hierarchy.length;
    hierarchy.forEach(g => {
      if (g.subMenus && Array.isArray(g.subMenus)) {
        totalTasks += g.subMenus.length;
        g.subMenus.forEach((s: any) => {
          if (s.menuLists && Array.isArray(s.menuLists)) {
            totalTasks += s.menuLists.length;
          }
        });
      }
      if (g.menuLists && Array.isArray(g.menuLists)) {
        totalTasks += g.menuLists.length;
      }
    });

    let completedTasks = 0;
    this.importProgress.set({ current: 0, total: totalTasks, text: 'กำลังเริ่มนำเข้าข้อมูล...' });

    try {
      const [existingGroupDtos, existingSubDtos, existingMenuDtos] = await lastValueFrom(
        forkJoin([
          this.apiService.getGroupMenusForTable().pipe(catchError(() => of([]))),
          this.apiService.getGroupSubMenusForTable().pipe(catchError(() => of([]))),
          this.apiService.getMenuListsForTable().pipe(catchError(() => of([])))
        ])
      );

      for (const g of hierarchy) {
        this.importProgress.set({
          current: completedTasks,
          total: totalTasks,
          text: `กำลังประมวลผลกลุ่มเมนูหลัก: ${g.name}`
        });

        let targetGroupMenuId: number | null = null;
        const existingG = (existingGroupDtos || []).find(
          (e: GroupMenuDto) => e.name.trim().toLowerCase() === g.name.trim().toLowerCase()
        );

        const mode = this.importMode();

        if (existingG && mode === 'skip') {
          targetGroupMenuId = existingG.id;
        } else if (existingG && mode === 'update') {
          targetGroupMenuId = existingG.id;
          const payload: GroupMenuPayload = {
            name: g.name,
            status: g.status ?? true,
            number: g.number ?? existingG.number,
            userId: userId,
            menuId: menuId
          };
          await lastValueFrom(this.apiService.updateGroupMenu(payload, existingG.id).pipe(catchError(() => of(null))));
        } else {
          const payload: GroupMenuPayload = {
            name: g.name,
            status: g.status ?? true,
            number: g.number ?? 1,
            userId: userId,
            menuId: menuId
          };
          await lastValueFrom(this.apiService.createGroupMenu(payload).pipe(catchError(() => of(null))));

          const reFetchGroups = await lastValueFrom(this.apiService.getGroupMenusForTable().pipe(catchError(() => of([]))));
          const newlyCreatedG = (reFetchGroups || []).find(
            (e: GroupMenuDto) => e.name.trim().toLowerCase() === g.name.trim().toLowerCase()
          );
          if (newlyCreatedG) {
            targetGroupMenuId = newlyCreatedG.id;
          }
        }

        completedTasks++;

        if (!targetGroupMenuId) continue;

        if (g.subMenus && Array.isArray(g.subMenus)) {
          for (const s of g.subMenus) {
            this.importProgress.set({
              current: completedTasks,
              total: totalTasks,
              text: `กำลังประมวลผลเมนูย่อย: ${s.name}`
            });

            let targetSubMenuId: number | null = null;
            const existingS = (existingSubDtos || []).find(
              (e: GroupSubMenuDto) =>
                e.groupMenuId === targetGroupMenuId &&
                e.name.trim().toLowerCase() === s.name.trim().toLowerCase()
            );

            if (existingS && mode === 'skip') {
              targetSubMenuId = existingS.id;
            } else if (existingS && mode === 'update') {
              targetSubMenuId = existingS.id;
              const payload: GroupSubMenuPayload = {
                id: existingS.id,
                name: s.name,
                enable: s.enable ?? true,
                groupMenuId: targetGroupMenuId,
                number: s.number ?? existingS.number,
                icon: s.icon || 'folder',
                userId: userId,
                menuId: menuId
              };
              await lastValueFrom(this.apiService.updateGroupSubMenu(payload, existingS.id).pipe(catchError(() => of(null))));
            } else {
              const payload: GroupSubMenuPayload = {
                name: s.name,
                enable: s.enable ?? true,
                groupMenuId: targetGroupMenuId,
                number: s.number ?? 1,
                icon: s.icon || 'folder',
                userId: userId,
                menuId: menuId
              };
              await lastValueFrom(this.apiService.createGroupSubMenu(payload).pipe(catchError(() => of(null))));

              const reFetchSubs = await lastValueFrom(
                this.apiService.getGroupSubMenusForTable(targetGroupMenuId).pipe(catchError(() => of([])))
              );
              const newlyCreatedS = (reFetchSubs || []).find(
                (e: GroupSubMenuDto) => e.name.trim().toLowerCase() === s.name.trim().toLowerCase()
              );
              if (newlyCreatedS) {
                targetSubMenuId = newlyCreatedS.id;
              }
            }

            completedTasks++;

            if (s.menuLists && Array.isArray(s.menuLists)) {
              for (const m of s.menuLists) {
                this.importProgress.set({
                  current: completedTasks,
                  total: totalTasks,
                  text: `กำลังประมวลผลรายการเมนู: ${m.name}`
                });

                const existingM = (existingMenuDtos || []).find(
                  (e: MenuListDto) =>
                    e.groupMenuId === targetGroupMenuId &&
                    e.name.trim().toLowerCase() === m.name.trim().toLowerCase()
                );

                if (existingM && mode === 'skip') {
                  // Skip
                } else if (existingM && mode === 'update') {
                  const payload: MenuListPayload = {
                    id: existingM.id,
                    name: m.name,
                    status: m.status ?? true,
                    path: m.path || '',
                    pathId: m.pathId ?? null,
                    number: m.number ?? existingM.number,
                    groupMenuId: targetGroupMenuId,
                    groupSubId: targetSubMenuId,
                    userId: userId,
                    menuId: menuId
                  };
                  await lastValueFrom(this.apiService.updateMenuList(payload, existingM.id).pipe(catchError(() => of(null))));
                } else {
                  const payload: MenuListPayload = {
                    name: m.name,
                    status: m.status ?? true,
                    path: m.path || '',
                    pathId: m.pathId ?? null,
                    number: m.number ?? 1,
                    groupMenuId: targetGroupMenuId,
                    groupSubId: targetSubMenuId,
                    userId: userId,
                    menuId: menuId
                  };
                  await lastValueFrom(this.apiService.createMenuList(payload).pipe(catchError(() => of(null))));
                }

                completedTasks++;
              }
            }
          }
        }

        if (g.menuLists && Array.isArray(g.menuLists)) {
          for (const m of g.menuLists) {
            this.importProgress.set({
              current: completedTasks,
              total: totalTasks,
              text: `กำลังประมวลผลรายการเมนู: ${m.name}`
            });

            const existingM = (existingMenuDtos || []).find(
              (e: MenuListDto) =>
                e.groupMenuId === targetGroupMenuId &&
                e.name.trim().toLowerCase() === m.name.trim().toLowerCase()
            );

            if (existingM && mode === 'skip') {
              // Skip
            } else if (existingM && mode === 'update') {
              const payload: MenuListPayload = {
                id: existingM.id,
                name: m.name,
                status: m.status ?? true,
                path: m.path || '',
                pathId: m.pathId ?? null,
                number: m.number ?? existingM.number,
                groupMenuId: targetGroupMenuId,
                groupSubId: null,
                userId: userId,
                menuId: menuId
              };
              await lastValueFrom(this.apiService.updateMenuList(payload, existingM.id).pipe(catchError(() => of(null))));
            } else {
              const payload: MenuListPayload = {
                name: m.name,
                status: m.status ?? true,
                path: m.path || '',
                pathId: m.pathId ?? null,
                number: m.number ?? 1,
                groupMenuId: targetGroupMenuId,
                groupSubId: null,
                userId: userId,
                menuId: menuId
              };
              await lastValueFrom(this.apiService.createMenuList(payload).pipe(catchError(() => of(null))));
            }

            completedTasks++;
          }
        }
      }

      this.toast.success('นำเข้าข้อมูลโครงสร้างเมนูสำเร็จเรียบร้อย');
      this.closeImportModal();
      this.onRefresh();
    } catch (err) {
      console.error(err);
      this.toast.error('เกิดข้อผิดพลาดในการนำเข้าข้อมูลเมนู');
    } finally {
      this.isLoading.set(false);
    }
  }
}
